import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Loader } from '@progress/kendo-react-indicators';
import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import useSwal from '../hooks/useSwal';
import { CallDetail } from '../types/call';
import { InsightCallFilterMetrics, InsightsFilter } from '../types/insight';
import { CallFilter } from '../types/filters';
import { CallTableSortColumnEnum } from '../enums/CallTableSortColumnEnum';
import { Dates, getDateBasedOnFilterType } from '../utils/FilterDrawerUtil';
import moment from 'moment';
import callsService from '../services/calls.service';
import recordingsService from '../services/recordings.service';
import useTranslation from '../hooks/useTranslation';
import useLocale from '../hooks/useLocale';
import { Checkbox, RadioButton } from "@progress/kendo-react-inputs";

interface BaseExportCallsDialogProps {
    checkedRows: number[];
    setCheckedRows: React.Dispatch<React.SetStateAction<number[]>>;
    onClose: () => void;
    callData: CallDetail[] | undefined;
    totalRecords: number | undefined;
    filter?: InsightsFilter | CallFilter;
    handleClearAllRows: () => void;
}

interface WithInsightId extends BaseExportCallsDialogProps {
    insightId?: string;
    filterKey?: string;
    filterSubKey?: string;
    filterValue?: string;
    viewBy?: string;
    viewByFilterSubKey?: string;
    viewByFilterValue?: string;
}

const ExportCallsDialog: React.FC<WithInsightId> = ({
    checkedRows,
    setCheckedRows,
    onClose,
    callData,
    totalRecords,
    filter,
    insightId,
    filterKey,
    filterSubKey,
    filterValue,
    viewBy,
    viewByFilterSubKey,
    viewByFilterValue,
    handleClearAllRows
}) => {
    const trans = useTranslation("ExportCallsDialog");
    const localeCtx = useLocale();

    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [columns, setColumns] = useState({
        dateTime: true,
        recordedUser: true,
        participants: true,
        duration: true,
        flag: true,
        evaluation: true,
        sentiment: true,
        topics: true,
        numberOfComments: true,
        callOutcome: true,
        reasonCode: true,
        notes: true
    });
    const [recordType, setRecordType] = useState<"all" | "selected">(checkedRows?.length > 0 ? "selected" : "all");
    const [isDownloaded,setIsDownloaded]=useState<boolean>(false);
    const [error,setError]=useState<string>("");

    const isAnyChecked =
        !(totalRecords === 0 && recordType === "all") &&
        !((checkedRows?.length === 0 || checkedRows === undefined) && recordType === "selected")
        && (
            columns.dateTime || columns.recordedUser || columns.participants ||
            columns.duration || columns.flag || columns.evaluation ||
            columns.sentiment || columns.topics || columns.numberOfComments ||
            columns.callOutcome || columns.reasonCode || columns.notes
        );

    useEffect(() => {
        if (!localeCtx?.selectedLocale?.current.componentTranslations["ExportCallsDialog"]) {
            trans.fetchTranslations("ExportCallsDialog");
        }
    }, [localeCtx?.selectedLocale]);

    const handleColumnChange = (column: keyof typeof columns) => {
        setColumns((prev) => ({ ...prev, [column]: !prev[column] }));
    };

    const handleSubmit = async () => {
        try {
            setIsExporting(true);
            setIsDownloaded(false);
            setError("");

            let requestQuery = "";

            // 1. if recordType == "all"
            if (recordType === "all") {
                const filterValuesCache = sessionStorage.getItem("VoiceAiCallFilter");
                if (filterValuesCache !== null) {
                    let filterValues: CallFilter = JSON.parse(filterValuesCache);
                    filterValues = {
                        ...filterValues, page: {
                            skip: 0,
                            take: totalRecords,
                            totalRecords: totalRecords,
                            sort: {
                                sortField: CallTableSortColumnEnum.DateTime,
                                sortDirection: "desc",
                            },
                        }
                    };
                    requestQuery = callsService.createRequestQuery(requestQuery, filterValues);
                }

                else {
                    let filterValues: CallFilter = {
                        startDate: filter?.startDate,
                        endDate: filter?.endDate,
                        periodType: filter?.periodType ?? 0,
                        filterType: filter?.filterType ?? 0,
                        page: {
                            skip: 0,
                            take: totalRecords,
                            totalRecords: totalRecords,
                            sort: {
                                sortField: CallTableSortColumnEnum.DateTime,
                                sortDirection: "desc",
                            },
                        },
                    };

                    if (filterValues.periodType !== 4) {
                        const getFilterBasesDates: Dates = getDateBasedOnFilterType(
                            filterValues.periodType ?? 0,
                            filterValues.filterType ?? 0
                        );
                        filterValues = {
                            ...filterValues,
                            startDate: moment(getFilterBasesDates.tempStartDate).format(
                                "YYYY-MM-DD HH:mm:ss"
                            ),
                            endDate: moment(getFilterBasesDates.tempEndDate).format(
                                "YYYY-MM-DD HH:mm:ss"
                            ),
                        };
                    }
                    requestQuery = callsService.createRequestQuery(requestQuery, filterValues);
                }

                if (insightId) {
                    // Export 'All' from Home page with metrics
                    let metricFilter: InsightCallFilterMetrics = {
                        insightId: insightId ?? "",
                        filterKey: filterKey,
                        filterSubKey: filterSubKey,
                        filterValue: filterValue,
                        viewBy: viewBy,
                        viewByFilterSubKey: viewByFilterSubKey,
                        viewByFilterValue: viewByFilterValue
                    }

                    const queryFilters: string[] = [];
                    if (metricFilter.insightId) {
                        queryFilters.push(`insightId=${insightId}`);
                    }
                    if (metricFilter.filterKey && metricFilter.filterValue) {
                        let filterKeyValQuery = `filterKey=${metricFilter.filterKey}&filterValue=${metricFilter.filterValue}`;
                        if (metricFilter.filterSubKey) {
                            filterKeyValQuery += `&filterSubKey=${metricFilter.filterSubKey}`;
                        }
                        if (filterKeyValQuery.trim() !== "") queryFilters.push(filterKeyValQuery);
                    }
                    if (metricFilter.viewBy && metricFilter.viewByFilterValue) {
                        let viewBykeyValQuery = `viewBy=${metricFilter.viewBy}&viewByFilterValue=${metricFilter.viewByFilterValue}`;
                        if (metricFilter.viewByFilterSubKey) {
                            viewBykeyValQuery += `&viewByFilterSubKey=${metricFilter.viewByFilterSubKey}`;
                        }
                        if (viewBykeyValQuery.trim() !== "") queryFilters.push(viewBykeyValQuery);
                    }

                    requestQuery = requestQuery + '&' + queryFilters.join("&");
                }
                const res = await recordingsService.exportRecordingsBySearchFilters(
                    {
                        isDateTime: columns.dateTime,
                        isRecordedUser: columns.recordedUser,
                        isParticipants: columns.participants,
                        isDuration: columns.duration,
                        isFlag: columns.flag,
                        isEvaluation: columns.evaluation,
                        isSentiments: columns.sentiment,
                        isTopics: columns.topics,
                        isNumberOfComments: columns.numberOfComments,
                        isCallOutcome: columns.callOutcome,
                        isReasonCode: columns.reasonCode,
                        isNotes: columns.notes
                    },
                    requestQuery
                );
                try{
                    await handleDownload(res.blob);
                    setIsExporting(false);
                    setIsDownloaded(true);
                }catch(err){
                    setError("We're currently unable to download the file. Please try again later.")
                    setIsDownloaded(false);
                }
            }

            // 2. if recordType == "selected"
            if (recordType === "selected") {
                const res = await recordingsService.exportRecordingsByIds(
                    {
                        isDateTime: columns.dateTime,
                        isRecordedUser: columns.recordedUser,
                        isParticipants: columns.participants,
                        isDuration: columns.duration,
                        isFlag: columns.flag,
                        isEvaluation: columns.evaluation,
                        isSentiments: columns.sentiment,
                        isTopics: columns.topics,
                        isNumberOfComments: columns.numberOfComments,
                        isCallOutcome: columns.callOutcome,
                        isReasonCode: columns.reasonCode,
                        isNotes: columns.notes
                    },
                    checkedRows
                );
                try{
                    await handleDownload(res.blob);
                    setIsExporting(false);
                    setIsDownloaded(true);
                }catch(err){
                    setError("We're currently unable to download the file. Please try again later.")
                    setIsDownloaded(false);
                }
            }
        }

        catch (err) {
            if (err instanceof AxiosError) {
                setError("Exporting the recording(s) was unsuccessful. Please try again later.")
                setIsDownloaded(false);
            }
        }finally{
            setIsExporting(false);
            handleClearAllRows();
        }
    };

    const handleDownload = async (excelBlob:Blob) => {
        if (!excelBlob) throw new Error("We're currently unable to download the file. Please try again later.");

        const url = window.URL.createObjectURL(excelBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "EXPORT-Calls.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
        setCheckedRows([]);
        onClose();
    };

    return (
        <Dialog
            title={trans.fetchLabelKeyTranslation("ExportDataText", "Export data")}
            onClose={() => { if (!isExporting) onClose(); }}
            closeIcon={!isExporting}
            style={{ zIndex: 999 }}>
            <div className="export-modal h-100" style={{ minWidth: 300 }}>
                {(!isExporting && !isDownloaded && error) ? (
                    <div style={{display: "flex", justifyContent: "center", alignItems: "center", height: 200, textAlign:"center"}}>
                        <p style={{color:"red"}}>{error}</p>
                    </div>
                ) : (
                    <>
                        {isExporting ? (
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", height: 200 }}>
                                <p>{trans.fetchLabelKeyTranslation("FileExportingText", "Your Excel file is being created in .xlsx format. This may take a moment.")}</p>
                                <Loader size="large" type="infinite-spinner" />
                            </div>
                        ) : (
                            <div className="dialogContent">
                                {!isDownloaded  ? (
                                    <>
                                        <div className="formBoxRow fs-14 font-weight-bold mb-2">
                                            {trans.fetchLabelKeyTranslation("SelectRecordsText", "Select records to export")}
                                        </div>
                                        <div className="mb-3">
                                            <label className="d-block mb-1">
                                                <RadioButton
                                                    name="recordType"
                                                    value="all"
                                                    checked={recordType === "all"}
                                                    onChange={() => setRecordType("all")}
                                                />{" "}
                                                {trans.fetchLabelKeyTranslation("AllRecordsText", "All Records")}: <b>{totalRecords}</b>
                                            </label>
                                            <label className="d-block">
                                                <RadioButton
                                                    name="recordType"
                                                    value="selected"
                                                    checked={recordType === "selected"}
                                                    onChange={() => setRecordType("selected")}
                                                    disabled={(checkedRows?.length === undefined || checkedRows?.length === 0) ? true : false}
                                                />{" "}
                                                {trans.fetchLabelKeyTranslation("SelectedRecordsText", "Selected Records")}: <b>{checkedRows?.length ?? 0}</b>
                                            </label>
                                        </div>

                                        <div className="formBoxRow fs-14 font-weight-bold mb-2">
                                            {trans.fetchLabelKeyTranslation("SelectColumnsText", "Select columns to export")}
                                        </div>
                                        <div className="mb-2 column-select">
                                            <div className='row row-cols-2 no-gutters'>
                                                <div className='col-6'>
                                                    <label className="d-block mb-1">
                                                        <Checkbox
                                                            checked={columns.dateTime}
                                                            onChange={() => handleColumnChange("dateTime")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("DateTimeText", "Date Time")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block mb-1">
                                                        <Checkbox
                                                            checked={columns.recordedUser}
                                                            onChange={() => handleColumnChange("recordedUser")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("RecordedUserText", "Recorded User")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.participants}
                                                            onChange={() => handleColumnChange("participants")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("ParticipantsText", "Participants")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.duration}
                                                            onChange={() => handleColumnChange("duration")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("DurationText", "Duration")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.flag}
                                                            onChange={() => handleColumnChange("flag")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("FlagText", "Flag")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.evaluation}
                                                            onChange={() => handleColumnChange("evaluation")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("EvaluationText", "Evaluation")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.sentiment}
                                                            onChange={() => handleColumnChange("sentiment")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("SentimentText", "Sentiment")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.topics}
                                                            onChange={() => handleColumnChange("topics")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("TopicsText", "Topics")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.numberOfComments}
                                                            onChange={() => handleColumnChange("numberOfComments")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("NumberOfCommentsText", "Number Of Comments")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.callOutcome}
                                                            onChange={() => handleColumnChange("callOutcome")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("CallOutcomeText", "Call Outcome")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.reasonCode}
                                                            onChange={() => handleColumnChange("reasonCode")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("ReasonCodeText", "Reason Code")}
                                                    </label>
                                                </div>
                                                <div className='col-6'>
                                                    <label className="d-block">
                                                        <Checkbox
                                                            checked={columns.notes}
                                                            onChange={() => handleColumnChange("notes")}
                                                        />{" "}
                                                        {trans.fetchLabelKeyTranslation("NotesText", "Notes")}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: 200, padding: "1rem"}}>
                                        <p>{trans.fetchLabelKeyTranslation("SuccessText", "Your file has been download successsfully")}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {(!error && !isExporting && !isDownloaded) && (
                 <DialogActionsBar>
                    <button
                        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                        onClick={onClose}
                        disabled={isExporting}
                    >
                        {trans.fetchLabelKeyTranslation("CancelText", "Cancel")}
                    </button>
                    <Button
                        themeColor="primary"
                        fillMode="solid"
                        disabled={!isAnyChecked}
                        onClick={handleSubmit}
                    >
                       {trans.fetchLabelKeyTranslation("ExportText", "Export")}
                    </Button>
                </DialogActionsBar>
            )}
        </Dialog>
    );

    // return (
    //     <Dialog
    //         // className="dialogShareList"
    //         title="Export"
    //         onClose={onClose}
    //         style={{ zIndex: 999 }}
    //     >
    //         <div className="export-modal tab-line tabContentWidth-100 tabContentP-0  h-100">
    //             <div className="dialogRow h-100">
    //                 <div className="w-100 h-100">
    //                     <div className="dialogColInner h-100">
    //                         <div className="row">
    //                             <div className="col-12">
    //                                 <div className="formBoxRow fs-14 font-weight-bold">
    //                                     Select Records to export
    //                                 </div>
    //                             </div>
    //                         </div>
    //                         <div className="row mx-0 mt-1 mb-3">
    //                             <div className="col-12 mb-1">
    //                                 <label>
    //                                     <input
    //                                         type="radio"
    //                                         name="recordType"
    //                                         value="all"
    //                                         checked={recordType === "all"}
    //                                         onChange={() => setRecordType("all")}
    //                                     />{" "}
    //                                     All Records
    //                                     <>: {totalRecords}</>
    //                                 </label>
    //                             </div>
    //                             <div className="col-12">
    //                                 <label>
    //                                     <input
    //                                         type="radio"
    //                                         name="recordType"
    //                                         value="selected"
    //                                         checked={recordType === "selected"}
    //                                         onChange={() => setRecordType("selected")}
    //                                     />{" "}
    //                                     Selected Records
    //                                     <>{checkedRows === undefined || checkedRows.length == 0
    //                                         ? <>: 0</>
    //                                         : <>: {checkedRows?.length}</>
    //                                     }</>
    //                                 </label>
    //                             </div>
    //                         </div>
    //                         <div className="row">
    //                             <div className="col-12">
    //                                 <div className="formBoxRow fs-14 font-weight-bold">
    //                                     Select Columns to export
    //                                 </div>
    //                             </div>
    //                         </div>
    //                         <div className="column-select mb-1">

    //                             <div className="row mx-0 mt-1">
    //                                 <div className="col-12 mb-1">
    //                                     <label>
    //                                         <input
    //                                             type="checkbox"
    //                                             checked={columns.dateTime}
    //                                             onChange={() => handleColumnChange("dateTime")}
    //                                         />{" "}
    //                                         Call Time
    //                                     </label>
    //                                 </div>
    //                                 <div className="col-12 mb-1">
    //                                     <label>
    //                                         <input
    //                                             type="checkbox"
    //                                             checked={columns.callDuration}
    //                                             onChange={() => handleColumnChange("callDuration")}
    //                                         />{" "}
    //                                         Call Duration
    //                                     </label>
    //                                 </div>
    //                                 <div className="col-12 mb-1">
    //                                     <label>
    //                                         <input
    //                                             type="checkbox"
    //                                             checked={columns.recordedUser}
    //                                             onChange={() => handleColumnChange("recordedUser")}
    //                                         />{" "}
    //                                         Recorded User
    //                                     </label>
    //                                 </div>

    //                             </div>
    //                             {excelBlob &&
    //                                 <div style={{ paddingTop: "10px", textAlign: "center" }}>
    //                                     <Button onClick={handleDownload} themeColor="success">
    //                                         Click here to download Excel
    //                                     </Button>
    //                                 </div>
    //                             }
    //                         </div>
    //                     </div>
    //                 </div>
    //             </div>
    //         </div>

    //         <DialogActionsBar>
    //             <button
    //                 className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
    //                 onClick={onClose}
    //             >
    //                 Cancel
    //             </button>
    //             <Button
    //                 themeColor="primary"
    //                 fillMode="solid"
    //                 disabled={!isAnyChecked || isExporting}
    //                 onClick={handleSubmit}
    //             >
    //                 {isExporting ? (
    //                     <Loader size="small" type="infinite-spinner" themeColor="light" />
    //                 ) : (
    //                     "Export"
    //                 )}
    //             </Button>
    //         </DialogActionsBar>

    //     </Dialog>
    // );
};

export default ExportCallsDialog;
